import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  makeStyles,
  Textarea,
} from "@fluentui/react-components";
import {
  Dismiss16Regular,
  Dismiss24Regular,
  Send24Filled,
} from "@fluentui/react-icons";
import UploadIcon from "../../../../assets/uploadicon.svg";
import React, { useState } from "react";
import FormikInputRequest from "../../../../service/hook/useFormik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import Dropzone from "react-dropzone";
import {
  fetchCustomerMessages,
  postMessage,
} from "../../../../redux/clientPortal/clientPortalslice";
import DynamicDropzone from "../../../../components/DynamicDropzone/Dropzone";
import { getUserId } from "../../../../utils";

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

const useStyles = makeStyles({
  dialogContent: {
    padding: "10px",
    borderRadius: "4px",
  },
  attachmentContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 0",
  },
  chip: {
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    fontSize: "14px",
    color: "#616161",
    gap: "5px",
  },
});

function SendFile({ open, onClose, customerId, notify }: any) {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const [actionProcessing, setActionProcessing] = useState(false);
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const userId = getUserId();

  const { loading } = useSelector((state: RootState) => state.fileUpload);

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      notify(
        "File type not allowed. Allowed types: PDF, Word, Excel, images, text.",
        "",
        "warning",
      );
      return;
    }
    setRawFiles([file]);
    formikData.setFieldValue("file", file);
  };

  const removeFile = () => {
    setRawFiles([]);
    setUploadedFiles([]);
    formikData.setFieldValue("file", "");
  };

  const formikData = FormikInputRequest({
    initialValues: {
      subject: "",
      message: "",
      file: "",
    },
    validate: (values) => {
      const errors: any = {};
      if (values.message && values.message.length > 5000) {
        errors.message = "Description cannot be longer than 5000 characters";
      }
      if (!values.file) {
        errors.file = "File is required";
      }
      return errors;
    },
    onSubmitFn: async (values: any) => {
      const isScanning = uploadedFiles.some(
        (f) => f.status === "uploading" || f.status === "scanning",
      );
      if (isScanning) {
        notify("Please wait for file scanning to complete", "", "warning");
        return;
      }

      const hasFailed = uploadedFiles.some((f) => f.status === "error");
      if (hasFailed) {
        notify(
          "File failed security validation. Please attach a different file.",
          "",
          "error",
        );
        return;
      }

      const { subject, message } = values;
      const fileToSend =
        uploadedFiles.length > 0 ? uploadedFiles[0].file : null;

      const objectIds = uploadedFiles
        .filter((f) => f.objectId)
        .map((f) => f.objectId)
        .join(",");

      setActionProcessing(true);
      try {
        await dispatch(
          postMessage({
            customer_id: customerId,
            subject,
            message,
            sent_by_ifa: 1,
            isdocument_message: 1,
            // file: fileToSend,
            objectIds: objectIds || undefined,
          }),
        ).then((res: any) => {
          notify(res.payload.message, "", "success");
          handleClose();
          if (customerId) {
            dispatch(fetchCustomerMessages({ customerId }));
          }
        });
      } catch (error: any) {
        notify(error, "", "error");
      } finally {
        setActionProcessing(false);
      }
    },
  });

  const handleClose = () => {
    formikData.resetForm();
    setRawFiles([]);
    setUploadedFiles([]);
    onClose();
  };

  const isScanning = uploadedFiles.some(
    (f) => f.status === "uploading" || f.status === "scanning",
  );
  const hasFile = rawFiles.length > 0;

  return (
    <Dialog open={open} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={
                    <Dismiss24Regular
                      onClick={() => {
                        formikData.resetForm();
                        removeFile();
                        onClose();
                      }}
                    />
                  }
                />
              </DialogTrigger>
            }
          >
            Send File
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Field
              label={
                <span>
                  Upload File
                  {formikData.errors.file ? (
                    <span className="required-item">*</span>
                  ) : null}
                </span>
              }
              validationState={formikData.errors.file ? "error" : "none"}
              validationMessage={`${formikData?.errors?.file || ""}`}
            >
              <Dropzone onDrop={handleDrop} multiple={false}>
                {({ getRootProps, getInputProps }) => (
                  <section className="container">
                    <div
                      {...getRootProps({ className: "dropzone" })}
                      className="file-drop"
                      style={{
                        textAlign: "center",
                        border: "2px dashed #cdcdcd",
                        transition: "border 0.3s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.border = "2px dashed #000000a6")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.border = "2px dashed #cdcdcd")
                      }
                    >
                      <input {...getInputProps()} />
                      <img src={UploadIcon} width="50px" />
                      <p style={{ textAlign: "center" }}>
                        Click or Drag the file here to upload
                      </p>
                    </div>
                  </section>
                )}
              </Dropzone>
            </Field>

            {/* Upload/scan status rows replace the old static chip */}
            {hasFile && (
              <DynamicDropzone
                multiple={false}
                externalFiles={rawFiles}
                onFilesChange={(files: any) => {
                  setUploadedFiles(files);
                  if (files.length === 0) {
                    formikData.setFieldValue("file", "");
                  }
                }}
                userId={userId}
              />
            )}

            {/* Show remove option only when not uploading/scanning */}
            {/* {hasFile && !isScanning && (
              <div className="justify_between">
                <div className="list-upload-item">
                  {rawFiles[0]?.name} -{" "}
                  {(rawFiles[0]?.size / 1024).toFixed(2)} KB
                  <Dismiss16Regular onClick={removeFile} />
                </div>
              </div>
            )} */}

            <Field
              label="File Name"
              validationState={formikData.errors.subject ? "error" : "none"}
              validationMessage={`${formikData?.errors?.subject || ""}`}
            >
              <Input
                type="text"
                name="subject"
                value={formikData.values.subject}
                onChange={formikData.handleChange}
              />
            </Field>

            <Field
              label="Description"
              validationState={formikData.errors.message ? "error" : "none"}
              validationMessage={`${formikData?.errors?.message || ""}`}
            >
              <Textarea
                name="message"
                rows={5}
                value={formikData.values.message}
                onChange={formikData.handleChange}
              />
            </Field>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 10,
                margin: "10px 0",
              }}
            >
              <Button
                appearance="primary"
                className="asc-button-primary"
                icon={<Send24Filled />}
                disabled={
                  actionProcessing ||
                  loading ||
                  isScanning ||
                  Object.keys(formikData.errors).length > 0
                }
                onClick={() => formikData.handleSubmit()}
              >
                {actionProcessing
                  ? "Sending..."
                  : isScanning
                    ? "Scanning..."
                    : "Send"}
              </Button>
              <Button onClick={() => onClose()}>Cancel</Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default SendFile;
